import { ActionIcon, Button, Card, Grid, Group, Table, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Spinner from '../Spinner';
import BotIpRangeModal from './BotIpRangeModal';
import classes from './SuperAdminBotIpRanges.module.css';

const scopeLabel = (botIpRange) => {
  if (!botIpRange.domainId) {
    return 'All domains';
  }
  return botIpRange.domain ? `${botIpRange.domain.name} (#${botIpRange.domainId})` : `#${botIpRange.domainId}`;
};

const SuperAdminBotIpRanges = () => {
  const fetch = useFetch();
  const [loadError, setLoadError] = useState();
  const [botIpRanges, setBotIpRanges] = useState();
  const [editing, setEditing] = useState();
  const [confirmDeleteId, setConfirmDeleteId] = useState();

  const loadBotIpRanges = useCallback(() => {
    setBotIpRanges(undefined);
    fetch('/api/admin/botIpRanges')
      .then(({ botIpRanges: loaded }) => setBotIpRanges(loaded))
      .catch(setLoadError);
  }, [fetch]);

  const deleteRange = (id, done) => {
    fetch(`/api/admin/botIpRanges/${id}`, { method: 'DELETE' })
      .then(() => {
        loadBotIpRanges();
        done();
      })
      .catch(setLoadError);
  };

  useEffect(loadBotIpRanges, [loadBotIpRanges]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (botIpRanges === undefined) {
    return <Spinner />;
  }

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <BotIpRangeModal
          botIpRange={editing}
          onClose={() => setEditing(undefined)}
          onSave={() => {
            notifications.show({
              title: 'Bot IP range saved',
              message: 'The nightly bot classifier uses it from its next run.',
              color: 'success',
            });
            loadBotIpRanges();
          }}
        />
        <ConfirmationModal
          show={!!confirmDeleteId}
          close={() => setConfirmDeleteId(undefined)}
          confirm={(done) => deleteRange(confirmDeleteId, done)}
          message="Profiles flagged only by this range are released on the next classifier run."
        />
        <Text size="sm" c="dimmed" mb="sm">
          Address ranges of known scraper fleets. A profile on one of these ranges is excluded from bot-corrected MAU
          only if it also has a single page view and no engagement.
        </Text>
        <Table.ScrollContainer minWidth={700}>
          <Table highlightOnHover className={classes.table}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>CIDR</Table.Th>
                <Table.Th>Label</Table.Th>
                <Table.Th>Applies to</Table.Th>
                <Table.Th w="100%" className={classes.notes}>
                  Notes
                </Table.Th>
                <Table.Th>Added by</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {botIpRanges.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No bot IP ranges configured yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {botIpRanges.map((botIpRange) => (
                <Table.Tr key={botIpRange.id}>
                  <Table.Td>
                    <strong>{botIpRange.cidr}</strong>
                  </Table.Td>
                  <Table.Td>{botIpRange.label}</Table.Td>
                  <Table.Td>{scopeLabel(botIpRange)}</Table.Td>
                  <Table.Td className={classes.notes}>{botIpRange.notes}</Table.Td>
                  <Table.Td>{botIpRange.createdBy ? botIpRange.createdBy.email : 'Seeded'}</Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label="Edit" position="top">
                        <ActionIcon variant="subtle" aria-label="Edit" onClick={() => setEditing(botIpRange)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete" position="top">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label="Delete"
                          onClick={() => setConfirmDeleteId(botIpRange.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Grid>
          <Grid.Col span={{ base: 12 }} ta="right">
            <Button onClick={() => setEditing({})}>
              <IconPlus size={14} /> Add range
            </Button>
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default SuperAdminBotIpRanges;
